<template>
  <section class="page-wrap">
    <UiPageHero
      eyebrow="Vận hành"
      title="Tình trạng thiết bị"
      description="Theo dõi thiết bị tiếp nhận, chuyển giao và từ chối trong một màn hình."
    >
      <template #actions>
        <UiButton
          variant="white"
          icon="refresh"
          :disabled="loading"
          @click="reloadActiveTabAndCounts"
        >
          {{ loading ? "Đang tải..." : "Làm mới" }}
        </UiButton>
      </template>
    </UiPageHero>

    <div class="panel-pad mb-4">
      <div
        class="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between"
      >
        <div class="flex flex-wrap gap-2">
          <UiButton
            v-for="tab in tabs"
            :key="tab.key"
            icon="view"
            :variant="activeTab === tab.key ? 'primary' : 'secondary'"
            @click="setTab(tab.key)"
          >
            <span>{{ tab.label }}</span>
            <span
              class="ml-1 rounded-full px-1.5 py-0.5 text-[10px] leading-none"
              :class="
                activeTab === tab.key
                  ? 'bg-white/20 text-white'
                  : 'bg-brand-50 text-brand-700'
              "
            >
              {{ getTabCount(tab.key) }}
            </span>
          </UiButton>
        </div>
        <div
          class="grid w-full min-w-0 grid-cols-1 gap-3 md:grid-cols-2 xl:flex xl:w-auto xl:items-end"
        >
          <UiDatePicker
            v-model="dateFrom"
            class="min-w-0 xl:w-44"
            label="Từ ngày"
            @change="handleDateChange"
          />
          <UiDatePicker
            v-model="dateTo"
            class="min-w-0 xl:w-44"
            label="Đến ngày"
            @change="handleDateChange"
          />
          <UiInput
            v-model="query"
            class="min-w-0 md:col-span-2 xl:col-span-1 xl:w-72"
            placeholder="Tìm thiết bị, khoa/phòng..."
          />
          <UiButton
            v-if="activeTab === 'incoming' && selectedExportIds.length"
            class="w-full md:w-auto md:justify-self-start"
            icon="export"
            @click="openExportModal"
          >
            Xuất biên bản
          </UiButton>
        </div>
      </div>
    </div>

    <div class="table-wrap mt-4" :class="{ 'is-loading': loading }">
      <div v-if="loading" class="table-loading-overlay">
        <span class="table-spinner"></span>
        <span>Đang tải dữ liệu...</span>
      </div>
      <table class="data-table min-w-[2000px]">
        <thead>
          <tr>
            <th>STT</th>
            <th>
              <p @click="toggleSort('tenThietBi')">
                Tên thiết bị {{ sortMark("tenThietBi") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('maThietBi')">
                Mã thiết bị {{ sortMark("maThietBi") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('namTiepNhan')">
                Năm tiếp nhận {{ sortMark("namTiepNhan") }}
              </p>
            </th>
            <th v-if="activeTab === 'incoming'">
              <p @click="toggleSort('thoiGianCapNhat')">
                Thời gian cập nhật {{ sortMark("thoiGianCapNhat") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('giaTriTiepNhan')">
                Nguyên giá {{ sortMark("giaTriTiepNhan") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('giaTriHienTai')">
                Giá trị thời điểm tiếp nhận {{ sortMark("giaTriHienTai") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('tyLeSuDungConLai')">
                Tỷ lệ sử dụng còn lại {{ sortMark("tyLeSuDungConLai") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('yeuCauSuaChua')">
                Yêu cầu sửa chữa {{ sortMark("yeuCauSuaChua") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('khoaPhongTiepNhan')">
                Khoa phòng tiếp nhận {{ sortMark("khoaPhongTiepNhan") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('khoaPhongHienTai')">
                Khoa phòng hiện tại {{ sortMark("khoaPhongHienTai") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('loaiThietBi')">
                Loại thiết bị {{ sortMark("loaiThietBi") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('khoaPhongChuyenGiao')">
                Khoa phòng chuyển giao {{ sortMark("khoaPhongChuyenGiao") }}
              </p>
            </th>
            <th v-if="activeTab === 'rejected'">
              <p @click="toggleSort('lyDo')">Lý do {{ sortMark("lyDo") }}</p>
            </th>
            <th v-if="activeTab === 'rejected'">
              <p @click="toggleSort('thoiGianTuChoi')">
                Thời gian từ chối {{ sortMark("thoiGianTuChoi") }}
              </p>
            </th>
            <th v-if="activeTab === 'rejected'">
              <p @click="toggleSort('nguoiTuChoi')">
                Người từ chối {{ sortMark("nguoiTuChoi") }}
              </p>
            </th>
            <th v-if="activeTab === 'incoming'">Xuất BB bàn giao ↕</th>
            <th>Thao tác</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          <tr
            v-for="(item, index) in paginatedRows"
            :key="item.id"
            class="hover:bg-slate-50"
          >
            <td>{{ rowIndex(index) }}</td>
            <td class="font-semibold text-ink">{{ item.tenThietBi }}</td>
            <td>{{ item.maThietBi || "-" }}</td>
            <td>{{ getReceiptYear(item.thoiGianTiepNhan) }}</td>
            <td v-if="activeTab === 'incoming'">
              {{ formatDate(item.thoiGianCapNhat) }}
            </td>
            <td>{{ formatCurrency(item.giaTriTiepNhan) }}</td>
            <td>{{ formatCurrency(item.giaTriHienTai) }}</td>
            <td>{{ getRemainingUsage(item) }}</td>
            <td>
              <span :class="repairRequestClass(item)">
                {{ repairRequestLabel(item) }}
              </span>
            </td>
            <td>{{ item.khoaPhongTiepNhan || "-" }}</td>
            <td>{{ item.khoaPhongHienTai || "-" }}</td>
            <td>{{ item.loaiThietBi || item.loai || "-" }}</td>
            <td>{{ item.khoaPhongChuyenGiao || "-" }}</td>
            <td v-if="activeTab === 'rejected'">{{ item.lyDo || "-" }}</td>
            <td v-if="activeTab === 'rejected'">
              {{ formatDate(item.thoiGianTuChoi) }}
            </td>
            <td v-if="activeTab === 'rejected'">
              {{ item.nguoiTuChoi || "-" }}
            </td>
            <td v-if="activeTab === 'incoming'" class="text-center">
              <input
                :checked="isExportSelected(item.id)"
                type="checkbox"
                class="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                @change="toggleExportSelection(item.id, $event)"
              />
            </td>
            <td class="text-right">
              <div class="inline-flex w-max justify-end gap-2">
                <UiButton
                  variant="secondary"
                  size="sm"
                  icon="history"
                  @click="openHistory(item)"
                  >Lịch sử</UiButton
                >
                <UiButton
                  v-if="activeTab === 'incoming'"
                  variant="secondary"
                  size="sm"
                  icon="transfer"
                  @click="openTransfer(item)"
                  >Chuyển</UiButton
                >
                <UiButton
                  v-if="isTransferStatus(item)"
                  size="sm"
                  icon="confirm"
                  @click="confirmDevice(item)"
                  >Xác nhận</UiButton
                >
                <UiButton
                  v-if="isTransferStatus(item)"
                  variant="danger"
                  size="sm"
                  icon="reject"
                  @click="openReject(item)"
                  >Từ chối</UiButton
                >
                <UiButton
                  variant="danger"
                  size="sm"
                  icon="delete"
                  @click="openDelete(item)"
                  >Xóa</UiButton
                >
              </div>
            </td>
          </tr>
          <tr v-if="!filteredRows.length">
            <td
              :colspan="emptyColspan"
              class="py-10 text-center text-slate-500"
            >
              Không có dữ liệu.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <UiPagination
      v-model="page"
      :total="filteredRows.length"
      :page-size="pageSize"
    />

    <UiModal v-if="modal" :size="modal === 'history' ? 'xl' : 'md'">
      <form @submit.prevent="submitModal">
        <div class="mb-4 flex items-start justify-between">
          <div>
            <p class="text-xl font-bold text-ink">{{ modalTitle }}</p>
            <p class="text-sm text-slate-500">{{ selectedName }}</p>
          </div>
          <UiButton variant="ghost" size="sm" icon="close" @click="modal = ''"
            >Đóng</UiButton
          >
        </div>

        <div
          v-if="modal === 'history'"
          class="table-wrap"
          :class="{ 'is-loading': historyLoading }"
        >
          <div v-if="historyLoading" class="table-loading-overlay">
            <span class="table-spinner"></span>
            <span>Đang tải dữ liệu...</span>
          </div>
          <table class="data-table min-w-[1500px]">
            <thead>
              <tr>
                <th>Stt</th>
                <th>Trạng thái</th>
                <th>Tên thiết bị</th>
                <th>
                  <p @click="toggleHistorySort('khoaPhongGiao')">
                    Khoa phòng chuyển {{ historySortMark("khoaPhongGiao") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('thoiGianGiao')">
                    Thời gian chuyển {{ historySortMark("thoiGianGiao") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('khoaPhongNhan')">
                    Khoa phòng nhận {{ historySortMark("khoaPhongNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('thoiGianXacNhan')">
                    Thời gian nhận {{ historySortMark("thoiGianXacNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('nguoiChuyen')">
                    Người chuyển {{ historySortMark("nguoiChuyen") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('nguoiNhan')">
                    Người nhận {{ historySortMark("nguoiNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('giaTriTiepNhan')">
                    Giá trị ban đầu {{ historySortMark("giaTriTiepNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('giaTriHienTai')">
                    Giá trị thời điểm tiếp nhận
                    {{ historySortMark("giaTriHienTai") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('noiDung')">
                    Nội dung {{ historySortMark("noiDung") }}
                  </p>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr
                v-for="(item, index) in sortedHistory"
                :key="historyKey(item, index)"
              >
                <td>{{ index + 1 }}</td>
                <td>
                  <span :class="historyStatusClass(item)">
                    {{ historyStatusLabel(item) }}
                  </span>
                </td>
                <td class="font-semibold text-ink">
                  {{ item.tenThietBi || selectedName || "-" }}
                </td>
                <td>{{ item.khoaPhongGiao || "-" }}</td>
                <td>{{ formatDate(item.thoiGianGiao) }}</td>
                <td>{{ item.khoaPhongNhan || "-" }}</td>
                <td>{{ formatDate(item.thoiGianXacNhan) }}</td>
                <td>{{ item.nguoiChuyen || "-" }}</td>
                <td>{{ item.nguoiNhan || "-" }}</td>
                <td>{{ formatCurrency(item.giaTriTiepNhan) }}</td>
                <td>{{ formatCurrency(item.giaTriHienTai) }}</td>
                <td>{{ historyContent(item) }}</td>
              </tr>
              <tr v-if="!history.length">
                <td colspan="12" class="py-8 text-center text-slate-500">
                  Chưa có lịch sử.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="modal === 'transfer'">
          <UiSelect
            v-model.number="transferDepartmentId"
            label="Khoa/phòng nhận"
          >
            <option :value="0">Chọn khoa/phòng</option>
            <option v-for="item in departments" :key="item.id" :value="item.id">
              {{ item.tenKhoaPhong }}
            </option>
          </UiSelect>
          <div class="mt-4 flex justify-end gap-3">
            <UiButton variant="secondary" icon="cancel" @click="modal = ''"
              >Hủy</UiButton
            >
            <UiButton
              type="submit"
              icon="transfer"
              :disabled="!transferDepartmentId"
              >Chuyển thiết bị</UiButton
            >
          </div>
        </div>

        <div v-else-if="modal === 'reject'">
          <UiTextarea v-model="rejectReason" label="Lý do từ chối" required />
          <div class="mt-4 flex justify-end gap-3">
            <UiButton variant="secondary" icon="cancel" @click="modal = ''"
              >Hủy</UiButton
            >
            <UiButton
              type="submit"
              variant="danger"
              icon="reject"
              :disabled="!rejectReason"
              >Từ chối</UiButton
            >
          </div>
        </div>

        <div v-else-if="modal === 'delete'">
          <p class="text-sm font-semibold text-slate-600">
            Bạn có chắc chắn muốn xóa thiết bị
            <span class="font-bold text-ink">{{ selectedName }}</span>
            không?
          </p>
          <div class="mt-4 flex justify-end gap-3">
            <UiButton variant="secondary" icon="cancel" @click="modal = ''"
              >Hủy</UiButton
            >
            <UiButton
              type="submit"
              variant="danger"
              icon="delete"
              :loading="deletingDevice"
              >Xác nhận xóa</UiButton
            >
          </div>
        </div>

        <div v-else-if="modal === 'export'">
          <div class="grid gap-4 md:grid-cols-2">
            <UiInput
              v-model="exportForm.benGiao1"
              label="Bên bàn giao 1"
              required
            />
            <UiInput
              v-model="exportForm.chucVuBenGiao1"
              label="Chức vụ bàn giao 1"
              required
            />
            <UiInput
              v-model="exportForm.benGiao2"
              label="Bên bàn giao 2"
              required
            />
            <UiInput
              v-model="exportForm.chucVuBenGiao2"
              label="Chức vụ bàn giao 2"
              required
            />
            <UiInput
              v-model="exportForm.benNhan1"
              label="Bên nhận 1"
              required
            />
            <UiInput
              v-model="exportForm.chucVuBenNhan1"
              label="Chức vụ nhận 1"
              required
            />
            <UiInput
              v-model="exportForm.benNhan2"
              label="Bên nhận 2"
              required
            />
            <UiInput
              v-model="exportForm.chucVuBenNhan2"
              label="Chức vụ nhận 2"
              required
            />
            <UiInput
              v-model="exportForm.ghiChu"
              label="Ghi chú"
              class="md:col-span-2"
            />
          </div>
          <div class="mt-4 flex justify-end gap-3">
            <UiButton variant="secondary" icon="cancel" @click="modal = ''"
              >Hủy</UiButton
            >
            <UiButton
              type="submit"
              icon="export"
              :loading="exporting"
              loading-text="Đang xuất..."
            >
              Xuất
            </UiButton>
          </div>
        </div>
      </form>
    </UiModal>
  </section>
</template>

<script lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import QLTSRepository from "@/services/QLTSRepository";
import { ThietBi } from "@/models/thietBi";
import { KhoaPhong } from "@/models/khoaPhong";
import { LichSuBanGiao } from "@/models/lichSuBanGiao";
import { formatCurrency, formatDate, normalizeText } from "@/utils/format";
import { getErrorMessage, useToast } from "@/utils/toast";
import {
  compareSortValues,
  nextSortState,
  sortIndicator,
  SortState,
} from "@/utils/tableSort";

type TabKey = "incoming" | "transfer" | "rejected";

const uploadBaseUrl = "https://qlts.chuyendoisoquocgia.net/UploadFolder/";

const emptyExportForm = () => ({
  benGiao1: "",
  chucVuBenGiao1: "",
  benGiao2: "",
  chucVuBenGiao2: "",
  benNhan1: "",
  chucVuBenNhan1: "",
  benNhan2: "",
  chucVuBenNhan2: "",
  ghiChu: "",
});

const toUnixSeconds = (value?: string | Date | null): number => {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : Math.floor(date.getTime() / 1000);
};

export default {
  setup() {
    const { showToast } = useToast();
    const tabs = [
      { key: "incoming" as TabKey, label: "Đã tiếp nhận" },
      { key: "transfer" as TabKey, label: "Chờ tiếp nhận" },
      { key: "rejected" as TabKey, label: "Từ chối" },
    ];
    const activeTab = ref<TabKey>("incoming");
    const rows = ref<ThietBi[]>([]);
    const departments = ref<KhoaPhong[]>([]);
    const history = ref<LichSuBanGiao[]>([]);
    const historyLoading = ref(false);
    const selected = ref<ThietBi | null>(null);
    const selectedExportIds = ref<number[]>([]);
    const exportForm = ref(emptyExportForm());
    const exporting = ref(false);
    const deletingDevice = ref(false);
    const sortState = ref<SortState>({ key: "", direction: "asc" });
    const historySortState = ref<SortState>({ key: "", direction: "asc" });
    const modal = ref("");
    const query = ref("");
    const dateFrom = ref("");
    const dateTo = ref("");
    const loading = ref(false);
    const transferDepartmentId = ref(0);
    const rejectReason = ref("");
    const page = ref(1);
    const pageSize = 10;
    const tabCounts = ref<Record<TabKey, number>>({
      incoming: 0,
      transfer: 0,
      rejected: 0,
    });

    const range = () => {
      if (!dateFrom.value || !dateTo.value) return [0, 0];
      return [toUnixSeconds(dateFrom.value), toUnixSeconds(dateTo.value)];
    };
    const createTabRequest = (key: TabKey) => {
      const [t1, t2] = range();
      return key === "incoming"
        ? QLTSRepository.getListThietBiTiepNhan(t1, t2)
        : key === "transfer"
        ? QLTSRepository.getListThietBiChuyenGiao(t1, t2)
        : QLTSRepository.getListThietBiReject(t1, t2);
    };
    const updateTabCount = (key: TabKey, data: unknown) => {
      tabCounts.value[key] = Array.isArray(data) ? data.length : 0;
    };
    const getTabCount = (key: TabKey) => tabCounts.value[key] || 0;
    const refreshTabCounts = () => {
      Promise.allSettled(
        tabs.map((tab) =>
          createTabRequest(tab.key).then((res) => ({
            key: tab.key,
            data: res.data,
          })),
        ),
      )
        .then((results) => {
          results.forEach((result) => {
            if (result.status === "fulfilled") {
              updateTabCount(result.value.key, result.value.data);
            }
          });
        })
        .catch(() => undefined);
    };
    const loadActiveTab = () => {
      loading.value = true;
      const requestTab = activeTab.value;
      const request = createTabRequest(requestTab);
      request
        .then((res) => {
          updateTabCount(requestTab, res.data);
          rows.value = res.data || [];
          selectedExportIds.value = [];
        })
        .catch((err) => {
          rows.value = [];
          showToast(
            getErrorMessage(err, "Không tải được danh sách thiết bị."),
            "error",
          );
        })
        .finally(() => (loading.value = false));
    };
    const reloadActiveTabAndCounts = () => {
      loadActiveTab();
      refreshTabCounts();
    };

    const handleDateChange = () => {
      if (dateFrom.value && dateTo.value) {
        reloadActiveTabAndCounts();
      }
    };

    const setTab = (key: TabKey) => {
      activeTab.value = key;
      rows.value = [];
      selectedExportIds.value = [];
      loadActiveTab();
    };

    const filteredRows = computed(() => {
      const term = normalizeText(query.value);
      if (!term) return rows.value;
      return rows.value.filter((item) =>
        [
          item.tenThietBi,
          item.maThietBi,
          item.model,
          item.loaiThietBi,
          item.loai,
          item.khoaPhongHienTai,
          item.khoaPhongTiepNhan,
          item.khoaPhongChuyenGiao,
        ]
          .map(normalizeText)
          .some((value) => value.includes(term)),
      );
    });
    const getSortValue = (item: ThietBi, key: string) => {
      const map: Record<string, unknown> = {
        tenThietBi: item.tenThietBi,
        maThietBi: item.maThietBi,
        namTiepNhan: getReceiptYear(item.thoiGianTiepNhan),
        thoiGianCapNhat: item.thoiGianCapNhat,
        giaTriTiepNhan: item.giaTriTiepNhan,
        giaTriHienTai: item.giaTriHienTai,
        tyLeSuDungConLai: (item as any).tyLeSuDungConLai,
        yeuCauSuaChua: item.yeuCauSuaChua,
        khoaPhongTiepNhan: item.khoaPhongTiepNhan,
        khoaPhongHienTai: item.khoaPhongHienTai,
        loaiThietBi: item.loaiThietBi || item.loai,
        khoaPhongChuyenGiao: item.khoaPhongChuyenGiao,
        lyDo: item.lyDo,
        thoiGianTuChoi: item.thoiGianTuChoi,
        nguoiTuChoi: item.nguoiTuChoi,
      };
      return map[key];
    };
    const sortedRows = computed(() => {
      if (!sortState.value.key) return filteredRows.value;
      return [...filteredRows.value].sort((first, second) =>
        compareSortValues(
          getSortValue(first, sortState.value.key),
          getSortValue(second, sortState.value.key),
          sortState.value.direction,
        ),
      );
    });
    const paginatedRows = computed(() => {
      const start = (page.value - 1) * pageSize;
      return sortedRows.value.slice(start, start + pageSize);
    });
    const emptyColspan = computed(() => {
      if (activeTab.value === "transfer") return 13;
      if (activeTab.value === "rejected") return 16;
      return 15;
    });
    watch([query, activeTab, dateFrom, dateTo], () => {
      page.value = 1;
    });
    watch(filteredRows, () => {
      const maxPage = Math.max(
        1,
        Math.ceil(filteredRows.value.length / pageSize),
      );
      if (page.value > maxPage) page.value = maxPage;
    });

    const statusLabel = (item: ThietBi) => {
      if (item.tuChoi || activeTab.value === "rejected") return "Từ chối";
      if (item.tiepNhan) return "Tiếp nhận";
      return "Chuyển giao";
    };
    const isTransferStatus = (item: ThietBi) =>
      activeTab.value === "transfer" && !item.tuChoi && !item.tiepNhan;
    const statusClass = (item: ThietBi) => [
      "status-pill",
      item.tuChoi || activeTab.value === "rejected"
        ? "bg-rose-50 text-rose-700"
        : item.tiepNhan
        ? "bg-emerald-50 text-emerald-700"
        : "bg-brand-50 text-brand-700",
    ];
    const rowIndex = (index: number) => (page.value - 1) * pageSize + index + 1;
    const getReceiptYear = (value?: string | Date | number | null) => {
      if (!value) return "-";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "-" : String(date.getFullYear());
    };
    const getRemainingUsage = (item: ThietBi) => {
      const value = (item as any).tyLeSuDungConLai;
      if (value === null || value === undefined || value === "") return "-";
      return `${Number(value).toLocaleString("vi-VN", {
        maximumFractionDigits: 2,
      })}%`;
    };
    const repairRequestLabel = (item: ThietBi) =>
      item.yeuCauSuaChua ? "Có" : "Không";
    const repairRequestClass = (item: ThietBi) => [
      "status-pill",
      item.yeuCauSuaChua
        ? "bg-amber-50 text-amber-700"
        : "bg-slate-100 text-slate-700",
    ];
    const toggleSort = (key: string) => {
      sortState.value = nextSortState(sortState.value, key);
      page.value = 1;
    };
    const sortMark = (key: string) => sortIndicator(sortState.value, key);
    const historyKey = (item: LichSuBanGiao, index: number) =>
      `${item.idThietBi}-${item.idKhoaPhongGiao}-${item.idKhoaPhongNhan}-${index}`;
    const historyStatusLabel = (item: LichSuBanGiao) =>
      item.tuChoi ? "Từ chối" : item.thoiGianXacNhan ? "Đã nhận" : "Chờ nhận";
    const historyStatusClass = (item: LichSuBanGiao) => [
      "status-pill",
      item.tuChoi
        ? "bg-rose-50 text-rose-700"
        : item.thoiGianXacNhan
        ? "bg-emerald-50 text-emerald-700"
        : "bg-brand-50 text-brand-700",
    ];
    const historyContent = (item: LichSuBanGiao) =>
      item.lyDoTuChoi ||
      (item.tuChoi ? "Từ chối bàn giao" : "Bàn giao thiết bị");
    const getHistorySortValue = (item: LichSuBanGiao, key: string) => {
      const map: Record<string, unknown> = {
        khoaPhongGiao: item.khoaPhongGiao,
        thoiGianGiao: item.thoiGianGiao,
        khoaPhongNhan: item.khoaPhongNhan,
        thoiGianXacNhan: item.thoiGianXacNhan,
        nguoiChuyen: item.nguoiChuyen,
        nguoiNhan: item.nguoiNhan,
        giaTriTiepNhan: item.giaTriTiepNhan,
        giaTriHienTai: item.giaTriHienTai,
        noiDung: historyContent(item),
      };
      return map[key];
    };
    const sortedHistory = computed(() => {
      if (!historySortState.value.key) return history.value;
      return [...history.value].sort((first, second) =>
        compareSortValues(
          getHistorySortValue(first, historySortState.value.key),
          getHistorySortValue(second, historySortState.value.key),
          historySortState.value.direction,
        ),
      );
    });
    const toggleHistorySort = (key: string) => {
      historySortState.value = nextSortState(historySortState.value, key);
    };
    const historySortMark = (key: string) =>
      sortIndicator(historySortState.value, key);

    const openHistory = (item: ThietBi) => {
      selected.value = item;
      history.value = [];
      modal.value = "history";
      historyLoading.value = true;
      QLTSRepository.getListLichSuBanGiaoById(item.id)
        .then((res) => (history.value = res.data || []))
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được lịch sử bàn giao."),
            "error",
          );
        })
        .finally(() => (historyLoading.value = false));
    };
    const openTransfer = (item: ThietBi) => {
      selected.value = item;
      transferDepartmentId.value = 0;
      modal.value = "transfer";
    };
    const openReject = (item: ThietBi) => {
      selected.value = item;
      rejectReason.value = "";
      modal.value = "reject";
    };
    const openDelete = (item: ThietBi) => {
      selected.value = item;
      modal.value = "delete";
    };
    const confirmDevice = (item: ThietBi) =>
      QLTSRepository.confirmThietBi(item.id)
        .then(() => {
          showToast("Đã tiếp nhận thiết bị.", "success");
          reloadActiveTabAndCounts();
        })
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không xác nhận được thiết bị."),
            "error",
          );
        });
    const submitModal = () => {
      if (modal.value === "export") {
        exportSelected();
        return;
      }
      if (!selected.value) return;
      if (modal.value === "delete") {
        deletingDevice.value = true;
        QLTSRepository.deleteThietBi(selected.value.id)
          .then(() => {
            showToast("Đã xóa thiết bị.", "success");
            modal.value = "";
            reloadActiveTabAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không xóa được thiết bị."),
              "error",
            );
          })
          .finally(() => (deletingDevice.value = false));
        return;
      }
      if (modal.value === "transfer") {
        QLTSRepository.tranferThietBi(
          selected.value.id,
          transferDepartmentId.value,
        )
          .then(() => {
            showToast("Đã chuyển thiết bị.", "success");
            modal.value = "";
            reloadActiveTabAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không chuyển được thiết bị."),
              "error",
            );
          });
      }
      if (modal.value === "reject") {
        QLTSRepository.rejectThietBi({
          id: selected.value.id,
          lyDo: rejectReason.value,
        } as any)
          .then(() => {
            showToast("Đã từ chối thiết bị.", "success");
            modal.value = "";
            reloadActiveTabAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không từ chối được thiết bị."),
              "error",
            );
          });
      }
    };
    const modalTitle = computed(() =>
      modal.value === "history"
        ? "Lịch sử bàn giao"
        : modal.value === "transfer"
        ? "Chuyển thiết bị"
        : modal.value === "export"
        ? "Tạo biên bản bàn giao tài sản"
        : modal.value === "delete"
        ? "Xóa thiết bị"
        : "Từ chối thiết bị",
    );
    const selectedName = computed(() =>
      modal.value === "export"
        ? `${selectedExportRows.value.length} thiết bị được chọn`
        : selected.value
        ? selected.value.tenThietBi
        : "",
    );
    const selectedExportRows = computed(() =>
      rows.value.filter((item) => selectedExportIds.value.includes(item.id)),
    );
    const isExportSelected = (id: number) =>
      selectedExportIds.value.includes(id);
    const toggleExportSelection = (id: number, event: Event) => {
      const checked = (event.target as HTMLInputElement).checked;
      selectedExportIds.value = checked
        ? Array.from(new Set([...selectedExportIds.value, id]))
        : selectedExportIds.value.filter((itemId) => itemId !== id);
    };
    const openExportModal = () => {
      if (!selectedExportRows.value.length) return;
      exportForm.value = emptyExportForm();
      modal.value = "export";
    };
    const resolvePdfUrl = (value: string) =>
      /^https?:\/\//i.test(value) ? value : `${uploadBaseUrl}${value}`;
    const exportSelected = () => {
      if (!selectedExportRows.value.length) return;
      exporting.value = true;
      QLTSRepository.createBanGiao({
        ...exportForm.value,
        listThietBi: selectedExportRows.value,
      })
        .then((res) => {
          if (res.data) window.open(resolvePdfUrl(String(res.data)), "_blank");
          showToast("Đã tạo biên bản bàn giao.", "success");
          selectedExportIds.value = [];
          modal.value = "";
        })
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không xuất được biên bản bàn giao."),
            "error",
          );
        })
        .finally(() => (exporting.value = false));
    };

    onMounted(() => {
      QLTSRepository.getListKhoaPhong()
        .then((res) => (departments.value = res.data || []))
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được danh sách khoa/phòng."),
            "error",
          );
        });
      loadActiveTab();
      refreshTabCounts();
    });

    return {
      tabs,
      activeTab,
      getTabCount,
      setTab,
      rows,
      filteredRows,
      sortedRows,
      paginatedRows,
      emptyColspan,
      page,
      pageSize,
      query,
      dateFrom,
      dateTo,
      loading,
      loadActiveTab,
      reloadActiveTabAndCounts,
      handleDateChange,
      formatDate,
      formatCurrency,
      rowIndex,
      getReceiptYear,
      getRemainingUsage,
      repairRequestLabel,
      repairRequestClass,
      toggleSort,
      sortMark,
      historyKey,
      historyStatusLabel,
      historyStatusClass,
      historyContent,
      sortedHistory,
      toggleHistorySort,
      historySortMark,
      departments,
      selected,
      selectedExportIds,
      exportForm,
      exporting,
      deletingDevice,
      modal,
      modalTitle,
      selectedName,
      history,
      historyLoading,
      openHistory,
      openTransfer,
      openReject,
      openDelete,
      confirmDevice,
      transferDepartmentId,
      rejectReason,
      submitModal,
      statusLabel,
      isTransferStatus,
      statusClass,
      isExportSelected,
      toggleExportSelection,
      openExportModal,
      exportSelected,
    };
  },
};
</script>
